import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Download, CheckCircle2, FileText, ChevronRight, MessageCircle, Calendar } from "lucide-react";
import useCanonical from "../hooks/useCanonical";
import useJsonLd from "../hooks/useJsonLd";

const DOCUMENTS = [
  "Form 16 from employer",
  "PAN Card",
  "Aadhaar Card",
  "Bank statements (6-12 months)",
  "Investment proofs (80C, 80D, etc.)",
  "Home loan documents (if applicable)",
  "Rent receipts (if claiming HRA)",
  "Medical insurance premium receipts",
  "Education loan documents (80E, if applicable)",
  "Previous year ITR copy",
];

const DEDUCTIONS = [
  "Section 80C (Insurance, PPF, ELSS) — max ₹1,50,000",
  "Section 80D (Health insurance) — max ₹25,000 (₹50,000 senior citizen)",
  "HRA exemption (if applicable)",
  "Section 24b (Home loan interest) — max ₹2,00,000",
  "Section 80E (Education loan interest)",
  "Standard deduction — ₹75,000 (salaried, new regime)",
];

const buildChecklistText = (name) => `WINTRUST SOLUTIONS — ITR Filing Checklist
Prepared for: ${name}
Coimbatore | Tamil Nadu
https://wintrustsolutions.com

====================================================
DOCUMENTS NEEDED
====================================================
${DOCUMENTS.map((d) => `[ ] ${d}`).join("\n")}

====================================================
DEDUCTIONS TO CLAIM
====================================================
${DEDUCTIONS.map((d) => `[ ] ${d}`).join("\n")}

====================================================
TIMELINE
====================================================
Due date        : 31st July 2026 (for FY 2024-25)
Processing time : 2-3 weeks after filing

====================================================
NEXT STEPS
====================================================
Need help filing your ITR?
WhatsApp : +91 89409 88776
Email    : wintrustsolutions@gmail.com
Service  : https://wintrustsolutions.com/itr-filing

This checklist is for reference only. Consult a tax
expert for personalised advice.
`;

const downloadTxt = (filename, text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ITRChecklistPage = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useCanonical("/itr-checklist");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ITR Checklist for Salaried Employees — Download Free",
    "description": "Free downloadable ITR filing checklist for salaried employees in Coimbatore. Documents, deductions, and timeline for FY 2024-25.",
    "url": "https://wintrustsolutions.com/itr-checklist",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!name.trim()) errs.name = "Full name is required";
    if (!phone.trim()) errs.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone.trim())) errs.phone = "Enter a valid 10-digit phone";
    if (!email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = "Enter a valid email";

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Download text checklist locally
    downloadTxt(`ITR-Checklist-${name.trim().replace(/\s+/g, "-")}.txt`, buildChecklistText(name.trim()));

    // Open WhatsApp
    const waUrl =
      `https://wa.me/918940988776?text=` +
      `Hi%20WinTrust%20Solutions%2C%0A%0A` +
      `I%20downloaded%20the%20ITR%20Checklist.%20My%20name%20is%20${encodeURIComponent(name.trim())}%20and%20phone%20is%20%2B91${encodeURIComponent(phone.trim())}.%20Can%20you%20help%20me%20file%20my%20ITR%3F`;
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>ITR Checklist for Salaried Employees — Download Free | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Free downloadable ITR filing checklist for salaried employees in Coimbatore. Documents, deductions and timeline for FY 2024-25 ITR filing." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">ITR Checklist</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="itr-checklist-h1">
            Download Your ITR Filing Checklist
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Before filing your Income Tax Return, gather these documents. Our checklist ensures you don't miss any deduction or document.
          </p>
        </div>
      </section>

      {/* Download Form + Checklist side-by-side */}
      <section className="bg-navy pb-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Download Form */}
            <div
              className="bg-white rounded-xl p-6 md:p-8 shadow-xl h-fit lg:sticky lg:top-28"
              data-testid="itr-download-form-container"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gold/10 text-gold rounded-lg flex items-center justify-center">
                  <Download size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-navy font-heading">Get Your Free Checklist</h2>
                  <p className="text-slate-500 text-xs">PDF-style checklist + WhatsApp support</p>
                </div>
              </div>

              {submitted ? (
                <div
                  className="bg-green-50 border border-green-200 rounded-lg p-5 flex items-start gap-3"
                  data-testid="itr-download-success"
                >
                  <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                  <div>
                    <p className="text-navy font-semibold mb-1">Checklist downloaded. Opening WhatsApp...</p>
                    <p className="text-slate-600 text-sm">We'll also respond within 2 hours if you need personal filing help.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="itr-download-form">
                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="itr-name">
                      Full Name <span className="text-gold">*</span>
                    </label>
                    <input
                      id="itr-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="Your full name"
                      data-testid="itr-name-input"
                    />
                    {errors.name && <p className="text-red-600 text-xs mt-1" data-testid="itr-name-error">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="itr-phone">
                      Phone Number <span className="text-gold">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg text-slate-600 text-sm font-medium">+91</span>
                      <input
                        id="itr-phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-r-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                        placeholder="10-digit number"
                        data-testid="itr-phone-input"
                      />
                    </div>
                    {errors.phone && <p className="text-red-600 text-xs mt-1" data-testid="itr-phone-error">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="itr-email">
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      id="itr-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="you@example.com"
                      data-testid="itr-email-input"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1" data-testid="itr-email-error">{errors.email}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gold hover:bg-gold/90 text-navy font-semibold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
                    data-testid="itr-download-btn"
                  >
                    <Download size={18} />
                    Download Checklist
                  </button>

                  <p className="text-slate-500 text-xs text-center">
                    You'll get the checklist as a .txt file and be redirected to WhatsApp.
                  </p>
                </form>
              )}
            </div>

            {/* Checklist Preview */}
            <div className="text-white space-y-8" data-testid="itr-checklist-preview">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <FileText className="text-gold" size={22} />
                  <h3 className="text-xl font-semibold font-heading">Documents Needed</h3>
                </div>
                <ul className="space-y-2.5">
                  {DOCUMENTS.map((doc) => (
                    <li key={doc} className="flex items-start gap-3">
                      <CheckCircle2 className="text-gold flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-slate-200 text-sm md:text-base">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <FileText className="text-gold" size={22} />
                  <h3 className="text-xl font-semibold font-heading">Deductions to Claim</h3>
                </div>
                <ul className="space-y-2.5">
                  {DEDUCTIONS.map((d) => (
                    <li key={d} className="flex items-start gap-3">
                      <CheckCircle2 className="text-gold flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-slate-200 text-sm md:text-base">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <Calendar className="text-gold" size={22} />
                  <h3 className="text-xl font-semibold font-heading">Timeline</h3>
                </div>
                <div className="space-y-3 text-slate-200 text-sm md:text-base">
                  <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                    <span className="text-slate-400">Due date</span>
                    <span className="font-semibold text-white">31st July 2026 <span className="text-slate-400 text-xs">(FY 2024-25)</span></span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Processing time</span>
                    <span className="font-semibold text-white">2–3 weeks after filing</span>
                  </div>
                </div>
              </div>

              {/* Service link */}
              <div className="flex items-center gap-2">
                <Link
                  to="/itr-filing"
                  className="text-gold font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
                  data-testid="link-itr-filing"
                >
                  Need help filing? View our ITR Filing Services <ChevronRight size={16} />
                </Link>
              </div>

              {/* WhatsApp fallback CTA */}
              <a
                href="https://wa.me/918940988776?text=Hi%20WinTrust%20Solutions%2C%20I%20need%20help%20with%20ITR%20filing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 btn-whatsapp"
                data-testid="itr-whatsapp-btn"
              >
                <MessageCircle size={18} /> Talk to a Tax Expert
              </a>

              {/* Disclaimer */}
              <p className="text-slate-400 text-xs max-w-2xl">
                <strong className="text-slate-200">Disclaimer:</strong> This checklist is for reference only. Consult a tax expert for personalised advice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ITRChecklistPage;
